import { Component, OnInit, Input, Output, EventEmitter, ViewChild, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { Subject } from 'rxjs/internal/Subject';

import { Applicant, ApplicationFormData, Job, OptIn, User } from '@app/models';
import { ApplicantService, AuthService, FileService, OptInService, ResumeService, SharedUtilsService, UserService } from '@app/services';

declare const gtag: Function;

@Component({
    selector: 'applicant-form',
    templateUrl: './applicant-form.component.html',
    styleUrls: ['./applicant-form.component.scss'],
    standalone: false
})
export class ApplicantFormComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  @Input() applicationType: string; // ..eg: apply, inquire
  @Input() jobPassedIn: Job;
  @Input() userPassedIn?: User;
  @Input() referringSrc?: string = null;

  @Output() userPassedInChanged = new EventEmitter();

  @ViewChild('file', {static: false}) file;
  public files: Set<File> = new Set();

  currentUser: User;
  uploadPath: string = 'resumes';
  currentApplicant: Applicant = null;
  phoneMask;
  statesOptions = [];
  zipMask; 
  formChecked: boolean = false;
  validForm: boolean = false;
  showSpinner: boolean = false;
  showMyResumeButton: boolean = false;
  filesAdded: boolean = false;
  fileName: string;
  resumePathToUse: string = null;
  siteKey: string;
  verifiedCaptcha: boolean = false;
  optIntoSms: boolean = false;
  optIntoEmailPhone: boolean = false;

  readonly SMS_OPT_IN_CONSENT_LANGUAGE = 'By checking, you consent to receive SMS messages from EDU Healthcare regarding job opportunities and related services at the provided number. Message and data rates may apply. You can opt out at any time by replying STOP or UNSUBSCRIBE to any message.';
  readonly EMAIL_PHONE_OPT_IN_CONSENT_LANGUAGE = 'By checking, you consent to receive phone calls and emails from EDU Healthcare regarding job opportunities and related services.';

  constructor(
    public _router: Router,
    private _applicantService: ApplicantService,
    private _authService: AuthService,
    private _fileService: FileService,
    private _optInService: OptInService,
    private _resumeService: ResumeService,
    private _sharedUtilsService: SharedUtilsService,
    private _userService: UserService
  ) {
    this.phoneMask = this._sharedUtilsService.phoneMask;
    this.statesOptions = this._sharedUtilsService.statesList;
    this.zipMask = this._sharedUtilsService.zipMask;
    this.siteKey = this._applicantService.siteKey;
  }

  ngOnInit() {
    this.checkForServiceFormData();
  }
  

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }  


  checkForServiceFormData() {
    // Checking to see if data was set before. Means user went to select resume. Use this data for form if present.
    const existingFormData: ApplicationFormData = this._applicantService.getStoredApplicationData();

    if (existingFormData && existingFormData !== undefined) {
      this.currentUser = (existingFormData.user && existingFormData.user !== undefined) ? existingFormData.user : new User();
      this.currentApplicant = (existingFormData.applicant && existingFormData.applicant !== undefined) ? existingFormData.applicant : new Applicant();

      // If a resume was set, then need to give user name to let them know what was selected and set filesAdded boolean to pass validation
      if (this.currentApplicant.resumeTitle && this.currentApplicant.resumeTitle !== undefined && this.currentApplicant.resumeTitle !== '') {
        this.fileName = this.currentApplicant.resumeTitle;
        this.filesAdded = true;
      }

      // Show user resume select button
      if (this.currentUser.resumes && this.currentUser.resumes.length > 0) this.showMyResumeButton = true;
    } else {
      this.initForm(); // No data was present so use default form init.
    }
  }


  initForm() {
    if (this.userPassedIn && this.userPassedIn._id !== undefined) {
      this.currentUser = {...this.userPassedIn};
      this.checkForUserResumes();
    } else {
      this.currentUser = new User();
    }

    // Don't want user to save form with init names set, which are "-" for both.
    if (this.currentUser.first_name && this.currentUser.first_name === '-') this.currentUser.first_name = '';
    if (this.currentUser.last_name && this.currentUser.last_name === '-') this.currentUser.last_name = '';

    this.currentApplicant = new Applicant();
    this.currentApplicant.applicationType = this.applicationType;
    this.currentApplicant.job = this.jobPassedIn;
  }


  async checkForUserResumes() {
    const userResumes = await this._resumeService.getByUser(this.userPassedIn._id).toPromise();
    if (userResumes && userResumes !== undefined) this.currentUser.resumes = userResumes;

    // Show user resume select button
    if (this.currentUser.resumes && this.currentUser.resumes.length > 0) this.showMyResumeButton = true;
  }


  checkForm() {
    let passesChecks = true;
    if (
      (!this.currentUser.first_name || this.currentUser.first_name === '' || this.currentUser.first_name === '-') ||
      (!this.currentUser.last_name || this.currentUser.last_name === '' || this.currentUser.last_name === '-') ||
      (!this.currentUser.phone || this.currentUser.phone === '') ||
      (!this.currentUser.email || this.currentUser.email === '') ||
      (!this.currentUser.zip || this.currentUser.zip === '') ||
      (this.applicationType === 'apply' && (!this.currentUser.address || this.currentUser.address === '')) ||
      (this.applicationType === 'apply' && (!this.currentUser.city || this.currentUser.city === '')) ||
      (this.applicationType === 'apply' && (!this.currentUser.state || this.currentUser.state === '')) ||
      (this.applicationType === 'apply' && !this.filesAdded)) passesChecks = false;

    this.validForm = passesChecks;
  }


  addFiles() {
    this.files = new Set();
    this.filesAdded = false;
    this.fileName = null;
    this.file.nativeElement.value = "";
    if (this.currentApplicant.resume_path) delete this.currentApplicant.resume_path; // User is selecting a different file, removing this
    this.file.nativeElement.click();
  }


  onFilesAdded() {
    this.filesAdded = true;
    const files: { [key: string]: File } = this.file.nativeElement.files;
    for (const key in files) {
        if (!isNaN(parseInt(key, 0))) {
            // console.log('files[key]: ', files[key]);
            if (files[key].name) this.fileName = files[key].name;
            this.files.add(files[key]);
        }
    }
  }


  cancelUpload() {
    this.filesAdded = false;
    this.files = new Set();
    this.fileName = null;
    this.file.nativeElement.value = "";
  }


  selectUserResume() {
    // Store form data and go to select a resume
    const formData = new ApplicationFormData();
    formData.user = this.currentUser;
    formData.applicant = this.currentApplicant;
    this._applicantService.setStoredApplicationData(formData);
    this._router.navigate(['/applicant-form/saved-resumes']);
  }


  clearForm() {
    this.cancelUpload();
    this.initForm();

    this.formChecked = false;
    this.validForm = false;
    this.showSpinner = false;
    this.fileName = null;
    this.resumePathToUse = null;
    this.verifiedCaptcha = false;

    grecaptcha.reset(); // Resets google captcha
  }


  // Fired after captcha is selected, or times out
  async resolved(captchaResponse: string) {
    const body = { captcha: captchaResponse }

    // Sending captchaResponse to backend to verify it with google
    const captchaVerification = await this._applicantService.verifyCaptcha(body).toPromise();
    // console.log('captchaVerification: ', captchaVerification);

    if (captchaVerification && captchaVerification !== undefined) {
      this.verifiedCaptcha = (captchaVerification.success && captchaVerification.success === true) ? true : false;
    } else {
      this._sharedUtilsService.showEduNotificationWithOptions("Captcha verification failed", 3000, "danger");
    }
  }


  // Fired if captcha has error or server issue
  errored(_err) {
    console.warn(`reCAPTCHA error encountered`);
    this._sharedUtilsService.showEduNotificationWithOptions("reCAPTCHA error encountered: " + _err, 3000, "danger");
  }


  renameFile(originalFile, newName) {
    return new File([originalFile], newName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
    });
  }


  async submitForm() {
    const userCopy = {...this.currentUser};

    const currentFiles = this.file.nativeElement.files; // This.files is always true, so using this to check if file was added below
    this.formChecked = true;

    // console.log('Current Files: ', currentFiles);

    const fileCopies = [...currentFiles];

    this.checkForm();

    if (!this.validForm) {
      this._sharedUtilsService.showEduNotificationWithOptions("Invalid fields", 3000, "danger");
      return;
    }

    // Captcha was not selected or has timed out
    if (!this.verifiedCaptcha) {
      this._sharedUtilsService.showEduNotificationWithOptions("Captcha needs to be selected", 3000, "danger");
      return;
    }

    if (this.validForm) {
      let processedFile = null;
      let fixedFileName = null;

      // console.log('Valid form...');

      if (currentFiles.length && this.filesAdded && (!this.currentApplicant.resume_path || this.currentApplicant.resume_path === '')) {
        this.showSpinner = true;

        processedFile = await this.processFile();

        // console.log('processedFile: ', processedFile);
        // console.log('Files: ', this.files);

        const fileToSend = (fileCopies && fileCopies.length) ? fileCopies[0] : null;

        if (fileToSend) {
          // console.log('fileToSend: ', fileToSend);

          fixedFileName = processedFile.split('/').pop();
          const fileCopy = this.renameFile(fileToSend, fixedFileName);

          await this._fileService.uploadAsIsToYeehro(fixedFileName, fileCopy).toPromise();
          // console.log('uploadToYeehro: ', uploadToYeehro);
        }

        if (processedFile && processedFile !== undefined) {
          this.currentApplicant.resume_path = processedFile;
          await this.userCheck();
        }
      } else {
        this.showSpinner = true;
        await this.userCheck();
      }

      const currentComments = this.currentApplicant.comments;
      const applyInNote = 'Job Ref No: ' + this.currentApplicant.job.job_id;

      this.currentApplicant.comments = `${applyInNote}. ${currentComments}`;

      const _yeehroDetails = {
        applicant: this.currentApplicant,
        user: userCopy,
        referrerSource: this.referringSrc,
        file: {
          name: fixedFileName,
          link: `/uploads/resumes/${fixedFileName}`
        }
      };

      // console.log('Sending applicant to yeehro: ', _yeehroDetails);

      await this._applicantService.sendToYeehro(_yeehroDetails).toPromise();
    } else {
      this._sharedUtilsService.showEduNotificationWithOptions("Form is invalid", 3000, "danger");
    }
  }


  processFile() {
    return new Promise(async(resolve, reject) => {
      const uploadedFiles = await this._fileService.uploadToSpecifedFolder(this.files, this.uploadPath).toPromise();

      const uploadedFile = uploadedFiles && uploadedFiles.length ? uploadedFiles[0] : null;
      // console.log("🚀 ~ file: resource-document-uploader.component.ts ~ line 469 ~ ResourceDocumentUploaderComponent ~ returnnewPromise ~ oneImage", oneImage)

      if (uploadedFile && uploadedFile !== undefined && uploadedFile['body'] && uploadedFile['body'].link) {
        this._sharedUtilsService.showNotification('File upload successful.');
        this.resumePathToUse = uploadedFile['body'].link;
        resolve(uploadedFile['body'].link);
      } else {
        this._sharedUtilsService.showErrorDialog('There was an issue uploading the file attached.');
        resolve(null);
      }
    });
  }


  userCheck() {
    return new Promise(async(resolve, reject) => {
      if (!this.userPassedIn || this.userPassedIn === undefined) {
        // Look for user in db with this email.
        const foundUser = await this._userService.getUserByEmail(this.currentUser.email).toPromise();
  
        if (foundUser && foundUser !== undefined) {
          this.updateUserFields(foundUser, false);
        } else {
          // No user exists, so create one
          const promisedUser = await this.createUserForApplicant();
  
          if (promisedUser && promisedUser !== undefined) {
            this.currentApplicant.user = promisedUser as User;
            await this.saveApplicant();
          } else {
            this._sharedUtilsService.showEduNotificationWithOptions("There was an issue with the form submission! Please try again later", 3000, "danger");
          }
        }
      } else {
        this.updateUserFields(this.userPassedIn, true);
      }

      console.log('User check complete...');

      resolve(null);
    });
  }


  async updateUserFields(_user: User, _isLoggedInUser: boolean) {
    const userToCheck: User = {..._user};
    userToCheck.first_name = this.currentUser.first_name;
    userToCheck.last_name = this.currentUser.last_name;
    userToCheck.phone = this.currentUser.phone;
    userToCheck.zip = this.currentUser.zip;

    if (this.applicationType === 'apply') {
      userToCheck.address = this.currentUser.address;
      userToCheck.city = this.currentUser.city;
      userToCheck.state = this.currentUser.state;
    }

    const updatedUser = await this._userService.updateUserForApplicant(userToCheck).toPromise();
    if (updatedUser && updatedUser !== undefined) {
      if (_isLoggedInUser) {
        this._authService.setCurrentlyLoggedInUser(updatedUser); // Setting logged user with updated user
        this.userPassedInChanged.next(updatedUser); // Telling parent that userPassed in has been updated
      }

      this.currentApplicant.user = updatedUser;
      await this.saveApplicant();
    } else {
      this.clearForm();
      this._sharedUtilsService.showEduNotificationWithOptions("There was an issue with the form submission! Please try again later", 3000, "danger");
    }
  }


  createUserForApplicant() {
    // Create user account before submitting
    return new Promise(async (resolve) => {
      let newUser = new User();
      newUser.first_name = this.currentUser.first_name;
      newUser.last_name = this.currentUser.last_name;
      newUser.email = this.currentUser.email;
      newUser.zip = this.currentUser.zip;
      newUser.userType = 'Applicant';
      newUser.phone = this.currentUser.phone;

      if (this.applicationType === 'apply') {
        newUser.address = this.currentUser.address;
        newUser.city = this.currentUser.city;
        newUser.state = this.currentUser.state;
      }

      const savedUser = await this._userService.createUserForApplicant(newUser).toPromise();
      resolve(savedUser);
    });
  }


  saveApplicant() {
    // Create user account before submitting
    return new Promise(async (resolve) => {
      if (this.validForm) {
        this.currentApplicant.smsOptIn = this.optIntoSms;
        this.currentApplicant.emailPhoneOptIn = this.optIntoEmailPhone;
        this.currentApplicant.referrerSource = this.referringSrc;

        const createdApplicant = await this._applicantService.create(this.currentApplicant).toPromise();

        if (createdApplicant && createdApplicant !== undefined) {
          // Check for and create opt-in record if user opted in
          if ((this.optIntoSms || this.optIntoEmailPhone) && this.currentApplicant.user) {
            await this.handleOptIn(this.currentApplicant.user);
          }

          this._applicantService.setApplicantToBeViewed(this.currentApplicant); // Setting saved applicant to be used to see if user has account on thank you page

          try {
            gtag('event', 'job_application_submitted', {
              event_category: 'Job Application',
              event_label: this.currentApplicant?.job?.profession || 'Unknown',
              job_id: this.currentApplicant?.job?.job_id || '',
              application_type: 'standard'
            });
          } catch (_e) {}

          // this.clearForm();
          this._router.navigate(['/thank-you']); // Go to thank you page
          this._sharedUtilsService.showNotification("Form submission successful");
        } else {
          // this.clearForm();
          this._sharedUtilsService.showEduNotificationWithOptions("There was an issue with the form submission! Please try again later", 3000, "danger");
        }
      }

      resolve(null);
    });
  }


  async handleOptIn(user: User) {
    try {
      // Check if user already has an opt-in record
      const existingOptIn = await this._optInService.getByEmail(this.currentUser.email).toPromise();

      if (!existingOptIn) {
        // No existing opt-in, create one. Confirmation email is sent server-side.
        const optIn = new OptIn();
        optIn.user = user._id || user;
        optIn.email = this.currentUser.email;
        optIn.phone = this.currentUser.phone;
        optIn.smsOptIn = this.optIntoSms;
        optIn.emailPhoneOptIn = this.optIntoEmailPhone;
        optIn.consentLanguage = [
          this.optIntoSms ? this.SMS_OPT_IN_CONSENT_LANGUAGE : null,
          this.optIntoEmailPhone ? this.EMAIL_PHONE_OPT_IN_CONSENT_LANGUAGE : null
        ].filter(Boolean).join(' ');

        await this._optInService.create(optIn).toPromise();
      }
    } catch (err) {
      console.log('Error handling opt-in: ', err);
    }
  }


  goToApplyPage() {
    const routeToUse = this._router.url + '/apply';
    this._router.navigate([routeToUse]);
  }

}
